import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import Header from '@/components/Header';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell
} from '@/components/ui/table';
import {
  Users, Pulse, EnvelopeOpen, ChartBar, ArrowLeft, SignOut
} from '@phosphor-icons/react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [activity, setActivity] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user === false) navigate('/login');
    if (user && user.role !== 'admin') navigate('/');
  }, [user, navigate]);

  useEffect(() => {
    if (user && user.role === 'admin') fetchData();
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, actRes, inqRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/admin/login-activity`, { withCredentials: true }),
        axios.get(`${API}/admin/inquiries`, { withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setActivity(actRes.data);
      setInquiries(inqRes.data);
    } catch (err) {
      console.error('Failed to fetch admin data', err);
    } finally {
      setLoading(false);
    }
  };

  if (!user || user.role !== 'admin') return null;

  const statCards = stats ? [
    { label: 'Total Users', value: stats.total_users, icon: <Users size={20} weight="bold" />, color: 'text-[#0052FF]', bg: 'bg-blue-50' },
    { label: 'Logins Today', value: stats.logins_today, icon: <Pulse size={20} weight="bold" />, color: 'text-[#10B981]', bg: 'bg-emerald-50' },
    { label: 'Total Inquiries', value: stats.total_inquiries, icon: <EnvelopeOpen size={20} weight="bold" />, color: 'text-amber-600', bg: 'bg-amber-50' },
    { label: 'New Inquiries', value: stats.new_inquiries, icon: <ChartBar size={20} weight="bold" />, color: 'text-purple-600', bg: 'bg-purple-50' },
  ] : [];

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />

      <div className="pt-[72px]">
        {/* Header */}
        <div className="bg-[#0B132B] py-10">
          <div className="w-full px-6 md:px-10 lg:px-16 xl:px-24 flex items-center justify-between">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#10B981] mb-1">Administration</p>
              <h1
                className="text-2xl sm:text-3xl font-bold text-white tracking-tight"
                style={{ fontFamily: 'Chivo, sans-serif' }}
              >
                Admin Dashboard
              </h1>
            </div>
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 px-4 py-2 border border-[#1E293B] text-slate-300 text-sm hover:bg-[#0F172A] transition-colors"
              data-testid="admin-back-home-btn"
            >
              <ArrowLeft size={16} />
              Back to Site
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="w-full px-6 md:px-10 lg:px-16 xl:px-24 -mt-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {statCards.map((s) => (
              <div key={s.label} className="bg-white border border-slate-200 p-5" data-testid={`admin-stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
                <div className="flex items-center gap-3 mb-2">
                  <span className={`p-2 ${s.bg}`}>{React.cloneElement(s.icon, { className: s.color })}</span>
                  <span className="text-xs tracking-[0.15em] uppercase font-bold text-[#475569]">{s.label}</span>
                </div>
                <p className="text-2xl font-black text-[#0B132B]" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  {loading ? '...' : s.value}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div className="w-full px-6 md:px-10 lg:px-16 xl:px-24 mt-8 pb-16">
          <Tabs defaultValue="users" className="w-full">
            <TabsList className="bg-white border border-slate-200 p-1 h-auto rounded-none" data-testid="admin-tabs">
              <TabsTrigger value="users" className="rounded-none data-[state=active]:bg-[#0052FF] data-[state=active]:text-white" data-testid="admin-tab-users">
                Registered Users
              </TabsTrigger>
              <TabsTrigger value="activity" className="rounded-none data-[state=active]:bg-[#0052FF] data-[state=active]:text-white" data-testid="admin-tab-activity">
                Login Activity
              </TabsTrigger>
              <TabsTrigger value="inquiries" className="rounded-none data-[state=active]:bg-[#0052FF] data-[state=active]:text-white" data-testid="admin-tab-inquiries">
                Contact Inquiries
              </TabsTrigger>
            </TabsList>

            {/* Users Tab */}
            <TabsContent value="users" className="mt-0">
              <div className="bg-white border border-slate-200 border-t-0" data-testid="admin-users-table">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-bold text-[#0B132B]">Name</TableHead>
                      <TableHead className="font-bold text-[#0B132B]">Email</TableHead>
                      <TableHead className="font-bold text-[#0B132B]">Role</TableHead>
                      <TableHead className="font-bold text-[#0B132B]">Registered</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.length === 0 && !loading ? (
                      <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-8">No users found</TableCell></TableRow>
                    ) : (
                      users.map((u) => (
                        <TableRow key={u._id || u.email}>
                          <TableCell className="font-medium">{u.name || '-'}</TableCell>
                          <TableCell>{u.email}</TableCell>
                          <TableCell>
                            <Badge variant={u.role === 'admin' ? 'default' : 'secondary'} className={u.role === 'admin' ? 'bg-[#0052FF] text-white' : ''}>
                              {u.role}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-slate-500 text-xs">{formatDate(u.created_at)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            {/* Activity Tab */}
            <TabsContent value="activity" className="mt-0">
              <div className="bg-white border border-slate-200 border-t-0" data-testid="admin-activity-table">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-bold text-[#0B132B]">Email</TableHead>
                      <TableHead className="font-bold text-[#0B132B]">Action</TableHead>
                      <TableHead className="font-bold text-[#0B132B]">IP</TableHead>
                      <TableHead className="font-bold text-[#0B132B]">Timestamp</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {activity.length === 0 && !loading ? (
                      <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-8">No activity yet</TableCell></TableRow>
                    ) : (
                      activity.map((a, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-medium">{a.email}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={
                              a.action === 'login' ? 'border-emerald-300 text-emerald-700' :
                              a.action === 'logout' ? 'border-red-300 text-red-700' :
                              'border-blue-300 text-blue-700'
                            }>
                              {a.action}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-slate-500 text-xs font-mono">{a.ip || '-'}</TableCell>
                          <TableCell className="text-slate-500 text-xs">{formatDate(a.timestamp)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            {/* Inquiries Tab */}
            <TabsContent value="inquiries" className="mt-0">
              <div className="bg-white border border-slate-200 border-t-0" data-testid="admin-inquiries-table">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-bold text-[#0B132B]">Name</TableHead>
                      <TableHead className="font-bold text-[#0B132B]">Email</TableHead>
                      <TableHead className="font-bold text-[#0B132B]">Service</TableHead>
                      <TableHead className="font-bold text-[#0B132B]">Message</TableHead>
                      <TableHead className="font-bold text-[#0B132B]">Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {inquiries.length === 0 && !loading ? (
                      <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">No inquiries yet</TableCell></TableRow>
                    ) : (
                      inquiries.map((inq, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-medium">{inq.name}</TableCell>
                          <TableCell>{inq.email}</TableCell>
                          <TableCell>
                            {inq.service_interest ? (
                              <Badge variant="secondary">{inq.service_interest}</Badge>
                            ) : '-'}
                          </TableCell>
                          <TableCell className="max-w-[200px] truncate text-slate-500 text-xs">{inq.message}</TableCell>
                          <TableCell className="text-slate-500 text-xs">{formatDate(inq.created_at)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
